import { useEffect, useRef, useState } from 'react';
import { createAnalyserFromElement, getSharedAudioNodes } from '@/utils/sharedAudioContext';

export default function WaveformV2({ audioElement, color = '#22d3ee', progressColor = '#06b6d4', height = 80 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const waveformDataRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (!audioElement || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size
    const resizeCanvas = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width;
        canvas.height = height;
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // PRE-RENDER FULL WAVEFORM when track loads
    const renderFullWaveform = async () => {
      if (!audioElement.src || waveformDataRef.current) return;

      try {
        // Fetch and decode audio to generate full waveform
        const response = await fetch(audioElement.src);
        const arrayBuffer = await response.arrayBuffer();
        
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const offlineContext = new AudioContext();
        const audioBuffer = await offlineContext.decodeAudioData(arrayBuffer);
        
        // Extract waveform data
        const rawData = audioBuffer.getChannelData(0);
        const samples = 1000; // Number of samples for visualization
        const blockSize = Math.floor(rawData.length / samples);
        const filteredData = [];
        
        for (let i = 0; i < samples; i++) {
          let blockStart = blockSize * i;
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(rawData[blockStart + j]);
          }
          filteredData.push(sum / blockSize);
        }
        
        // Normalize
        const multiplier = Math.max(...filteredData) ** -1;
        waveformDataRef.current = filteredData.map(n => n * multiplier);
        setIsRendered(true);
        
        console.log('Full waveform pre-rendered!');
      } catch (error) {
        console.error('Waveform rendering error:', error);
        // Fallback to real-time rendering
        setupAnalyser();
      }
    };

    // Setup audio analyser for real-time visualization using shared context
    const setupAnalyser = () => {
      if (analyserRef.current) return;
      const result = createAnalyserFromElement(audioElement, 4096);
      if (result) {
        result.analyser.smoothingTimeConstant = 0.85;
        analyserRef.current = result.analyser;
      }
    };

    // Render full waveform when metadata is loaded
    const handleMetadata = () => {
      renderFullWaveform();
    };

    if (audioElement.readyState >= 2) {
      renderFullWaveform();
    }

    audioElement.addEventListener('loadedmetadata', handleMetadata);

    // Setup analyser for real-time on first play
    const handlePlay = () => {
      setupAnalyser();
      try {
        const { context } = getSharedAudioNodes(audioElement);
        if (context.state === 'suspended') context.resume();
      } catch (_) {}
    };

    audioElement.addEventListener('play', handlePlay);

    // Draw function
    const draw = () => {
      if (!ctx || !canvas.width || !canvas.height) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      // Clear with black background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate progress
      const progress = audioElement.duration && isFinite(audioElement.duration)
        ? audioElement.currentTime / audioElement.duration 
        : 0;

      // Draw progress background
      ctx.fillStyle = progressColor + '30';
      ctx.fillRect(0, 0, canvas.width * progress, canvas.height);

      // Draw waveform - use pre-rendered if available
      if (waveformDataRef.current) {
        // Draw PRE-RENDERED FULL WAVEFORM
        const data = waveformDataRef.current;
        const barWidth = canvas.width / data.length;
        
        ctx.fillStyle = color;
        for (let i = 0; i < data.length; i++) {
          const barHeight = data[i] * canvas.height * 0.8;
          const x = i * barWidth;
          const y = (canvas.height - barHeight) / 2;
          
          // Draw bar
          ctx.fillRect(x, y, barWidth - 0.5, barHeight);
        }
      } else {
        // Fallback to real-time analyser
        const analyser = analyserRef.current;
        if (analyser && !audioElement.paused) {
          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          analyser.getByteTimeDomainData(dataArray);

          // Draw the waveform
          ctx.lineWidth = 2.5;
          ctx.strokeStyle = color;
          ctx.beginPath();

          const sliceWidth = canvas.width / bufferLength;
          let x = 0;

          for (let i = 0; i < bufferLength; i++) {
            const v = dataArray[i] / 128.0; // Normalize to 0-2
            const y = (v * canvas.height) / 2;

            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }

            x += sliceWidth;
          }

          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();
        } else {
          // Flat line when paused and no pre-render
          ctx.strokeStyle = color + '50';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(0, canvas.height / 2);
          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();
        }
      }

      // Draw playhead
      if (progress > 0) {
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 3;
        ctx.shadowColor = '#fbbf24';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(canvas.width * progress, 0);
        ctx.lineTo(canvas.width * progress, canvas.height);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Draw time markers
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px monospace';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
      ctx.shadowBlur = 4;
      
      const currentTime = formatTime(audioElement.currentTime);
      ctx.fillText(currentTime, 8, 18);
      
      if (audioElement.duration && isFinite(audioElement.duration)) {
        const remaining = audioElement.duration - audioElement.currentTime;
        const remainingText = `-${formatTime(remaining)}`;
        ctx.fillText(remainingText, canvas.width - 55, 18);
      }
      
      ctx.shadowBlur = 0;

      animationRef.current = requestAnimationFrame(draw);
    };

    // Start animation loop
    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resizeCanvas);
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('loadedmetadata', handleMetadata);
    };
  }, [audioElement, color, progressColor, height]);

  const formatTime = (seconds) => {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <canvas 
      ref={canvasRef} 
      className="w-full rounded cursor-pointer"
      style={{ height: `${height}px`, backgroundColor: '#000', display: 'block' }}
      onClick={(e) => {
        if (audioElement && audioElement.duration && isFinite(audioElement.duration)) {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const clickPosition = x / rect.width;
          audioElement.currentTime = clickPosition * audioElement.duration;
        }
      }}
    />
  );
}
